import { useEffect, useState } from "react";

import { api, AuthRequiredError } from "./api.js";
import type {
  AcquisitionData,
  EngagementData,
  EventsData,
  EventsSortColumn,
  EventsSortDir,
  FilterState,
  HealthData,
  InteractionSessionCohort,
  OverviewData,
  PresenceData,
  RetentionData,
  SessionsData,
  TabId,
} from "./types.js";

type TabData =
  | OverviewData
  | HealthData
  | AcquisitionData
  | EngagementData
  | RetentionData
  | SessionsData
  | EventsData
  | PresenceData;

interface UseAnalyticsDataResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Set when the API rejected the caller, so the shell can offer sign-in instead of an error. */
  authError: AuthRequiredError | null;
}

export function useAnalyticsData<T extends TabData>(
  tab: TabId,
  filters: FilterState,
  options?: {
    limit?: number;
    offset?: number;
    eventName?: string;
    propertiesContains?: string;
    propertiesContains2?: string;
    clientErrorBucket?: "warning" | "error";
    interactionSessionId?: string;
    interactionSessionCohort?: InteractionSessionCohort;
    sessionsListLimit?: number;
    sessionsListOffset?: number;
    showTemperatureEvents?: boolean;
    showUptimePulse?: boolean;
    hideAttractBasics?: boolean;
    dedupePopupTapMissed?: boolean;
    tempCelsiusMin?: number;
    tempCelsiusMaxExclusive?: number;
    sortBy?: EventsSortColumn;
      sortDir?: EventsSortDir;
      sessionsLongOnly?: boolean;
    sessionsPaidOnly?: boolean;
    reportingTimezone?: string;
  },
): UseAnalyticsDataResult<T> {
  const [data, setData] = useState<{ tab: TabId; value: T } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authError, setAuthError] = useState<AuthRequiredError | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const fetchers: Record<TabId, (f: FilterState) => Promise<TabData>> = {
      overview: async (f) => {
        const [health, retention, engagement] = await Promise.all([
          api.health(f),
          api.retention(f),
          api.engagement(f),
        ]);
        return { health, retention, engagement };
      },
      health: api.health,
      presence: api.presence,
      acquisition: api.acquisition,
      engagement: api.engagement,
      retention: api.retention,
      sessions: (f) =>
        api.sessions(f, {
          limit: options?.sessionsListLimit,
          offset: options?.sessionsListOffset,
          longOnly: options?.sessionsLongOnly,
          paidOnly: options?.sessionsPaidOnly,
        }),
      events: (f) => api.events(f, options),
    };

    const fetcher = fetchers[tab];

    fetcher(filters)
      .then((result) => {
        if (!cancelled) {
          setData({ tab, value: result as T });
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          if (err instanceof AuthRequiredError) {
            setAuthError(err);
          } else {
            setError(err instanceof Error ? err.message : "Unknown error");
          }
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    tab,
    filters.fleetId,
    filters.venueId,
    filters.deviceId,
    filters.gameUrl,
    filters.timeframe,
    filters.from,
    filters.to,
    options?.limit,
    options?.offset,
    options?.eventName,
    options?.propertiesContains,
    options?.propertiesContains2,
    options?.clientErrorBucket,
    options?.interactionSessionId,
    options?.interactionSessionCohort,
    options?.sessionsListLimit,
    options?.sessionsListOffset,
    options?.showTemperatureEvents,
    options?.showUptimePulse,
    options?.hideAttractBasics,
    options?.dedupePopupTapMissed,
    options?.tempCelsiusMin,
    options?.tempCelsiusMaxExclusive,
    options?.sortBy,
    options?.sortDir,
    options?.sessionsLongOnly,
    options?.sessionsPaidOnly,
    options?.reportingTimezone,
  ]);

  const currentData = data?.tab === tab ? data.value : null;
  return { data: currentData, loading: loading || data?.tab !== tab, error, authError };
}
